import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../App';
import api from '../services/api';
import './RideReassign.css';

const reasons = {
  reservation_admin_receipt_pending: 'Zuerst im Reservierungseingang einen Betrieb wählen.',
  reservation_partner_required: 'Die Reservierung ist noch keinem Betrieb bestätigt zugeordnet.',
  reservation_state_changed: 'Der Stand hat sich geändert. Bitte aktualisieren und erneut auswählen.',
  reservation_hard_gate: 'Ab zehn Minuten vor dem Termin ist keine neue Zuweisung mehr zulässig.',
  reservation_not_acceptable: 'Diese Reservierung kann nicht mehr neu zugewiesen werden.',
  bundle_atomic_required: 'Diese Kette kann nur gemeinsam einem Fahrer zugewiesen werden.',
  partner_legal_acceptance_required: 'Der Betrieb muss sein aktuelles Vertragspaket bestätigen.',
  admin_required: 'Der Admin-Zugang ist nicht mehr gültig. Bitte erneut anmelden.',
  // RESERVIERUNG-KOMPLETT-001: dieselben Gründe wie bei der Zuweisung selbst.
  bundle_member_in_ready_window: 'Ab 45 Minuten vor der ersten Fahrt ist die Kette nur noch über „Partner anbieten“ im Reiter Reservierungen neu zuweisbar.',
  customer_blocked: 'Fahrer hat diesen Kunden gesperrt',
  self_order_forbidden: 'Fahrer ist selbst der Besteller',
  reservation_chain_conflict: 'Überschneidet sich mit einer anderen Fahrt des Fahrers',
  reservation_driver_client_required: 'Fahrer-App unterstützt Reservierungen nicht',
};
const errorText = error => error?.response?.data?.message || reasons[error?.response?.data?.error]
  || error?.response?.data?.error || 'Der Stand ist unklar. Bitte aktualisieren und erneut versuchen.';

// Current business/eligibility/state come from the server. No local assignment or delivery inference.
export default function ReservationReassign({ rideId, onReassigned }) {
  const { user } = useAuth();
  const identity = JSON.stringify([rideId, user?.id || null]);
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const active = useRef(null), scope = useRef(0), loadSequence = useRef(0), inFlight = useRef(null);
  const load = useCallback(async (preserveError = false) => {
    if (active.current !== identity) return null;
    const generation = scope.current, sequence = ++loadSequence.current;
    setLoading(true);
    if (preserveError !== true) setError(null);
    try {
      const response = await api.get(`/admin/rides/${rideId}/reservation-assignment`);
      if (active.current !== identity || generation !== scope.current || sequence !== loadSequence.current) return null;
      setData(response.data); setChoice('');
      return response.data;
    } catch (failed) {
      if (active.current === identity && generation === scope.current && sequence === loadSequence.current) setError(errorText(failed));
      return null;
    } finally {
      if (active.current === identity && generation === scope.current && sequence === loadSequence.current) setLoading(false);
    }
  }, [identity, rideId]);
  useEffect(() => {
    active.current = identity; scope.current += 1; inFlight.current = null;
    setData(null); setChoice(''); setSaving(false); setNotice(null); setError(null); load();
    return () => { active.current = null; scope.current += 1; loadSequence.current += 1; };
  }, [identity, load]);
  const submit = async event => {
    event.preventDefault();
    if (!data?.selectable || !choice || loading || inFlight.current != null) return;
    const fleet = choice === 'fleet', driver = data.drivers.find(d => d.id === choice);
    if (!fleet && !driver?.eligible) return;
    if (!window.confirm(fleet
      ? `Reservierung ${data.ride_ids.join(', ')} an alle geeigneten Fahrer von ${data.partner.name} freigeben? Die bisherige Fahrerzuweisung wird aufgehoben. Die Fahrerannahme steht danach noch aus.`
      : `Reservierung ${data.ride_ids.join(', ')} ${driver.name} anbieten? Eine andere Fahrerzuweisung löst die bisherige ab und benötigt eine neue Fahrerannahme.`)) return;
    const generation = scope.current;
    inFlight.current = generation; setSaving(true); setError(null); setNotice(null);
    try {
      const response = await api.post(`/admin/rides/${rideId}/reservation-assignment`, {
        mode: fleet ? 'fleet' : 'driver', driver_id: fleet ? null : choice, expected_state: data.expected_state,
      });
      if (active.current !== identity || generation !== scope.current) return;
      const bundled = data.rides.some(ride => ride.bundle_id);
      if (response.data?.ok !== true || (bundled ? !response.data.state_token
          : response.data.assigned !== (fleet ? 'fleet' : 'driver'))) throw Error('unconfirmed_assignment');
      const current = await load();
      if (active.current !== identity || generation !== scope.current) return;
      setNotice(current ? 'Zuweisung gespeichert. Der aktuelle Serverstand wird angezeigt.' : 'Änderung gespeichert. Bitte den aktuellen Stand erneut laden.');
      if (current && onReassigned) onReassigned(response.data);
    } catch (failed) {
      if (active.current !== identity || generation !== scope.current) return;
      await load(true);
      if (active.current === identity && generation === scope.current) setError(errorText(failed));
    } finally {
      if (active.current === identity && generation === scope.current) { inFlight.current = null; setSaving(false); }
    }
  };
  return <section className="ride-reassign" aria-label="Reservierung zuweisen">
    <header className="ride-reassign-head"><div><h3>Reservierung zuweisen</h3><p className="ride-reassign-current">Aktueller Betrieb: {data?.partner?.name || 'noch nicht bestätigt'}</p></div>
      <button type="button" className="ride-reassign-refresh" aria-label="Zuweisung aktualisieren" onClick={() => { setNotice(null); load(); }} disabled={loading || saving}>Aktualisieren</button></header>
    {error && <p role="alert" className="ride-reassign-note is-error">{error}</p>}
    {notice && <p role="status" className="ride-reassign-note">{notice}</p>}
    {loading && <p>Stand wird geladen …</p>}
    {data?.rides.map(ride => <p key={ride.id} className="ride-reassign-note">
      <strong>{ride.pickup_address} → {ride.dropoff_address}</strong><br />
      {ride.driver_id ? `Angenommen von ${ride.driver_name || 'Fahrer'}`
        : ride.assigned_driver_id ? `Angebot an ${ride.assigned_driver_name || 'Fahrer'} · Annahme offen`
          : ride.released_to_fleet ? 'An alle geeigneten Fahrer freigegeben · Annahme offen' : 'Noch kein Fahrer zugewiesen'}
    </p>)}
    {data && !data.selectable && <p>{reasons[data.reason] || 'Der aktuelle Stand erlaubt keine Neuzuweisung. Bitte aktualisieren.'}
      {data.reason === 'reservation_admin_receipt_pending' && <> <a href="/reservierungen">Reiter Reservierungen öffnen</a></>}</p>}
    {data?.selectable && <form onSubmit={submit} className="ride-reassign-form">
      <label className="ride-reassign-search">Ziel auswählen <select style={{ flex: 1, minWidth: 0, border: 0, background: 'transparent', color: 'inherit', fontSize: 12 }} aria-label="Ziel der Reservierung" value={choice} onChange={e => { setChoice(e.target.value); setNotice(null); }} disabled={saving || loading}>
        <option value="">Bitte auswählen</option>
        {data.can_release_to_fleet && <option value="fleet">Alle geeigneten Fahrer des Betriebs</option>}
        {data.drivers.map(driver => <option key={driver.id} value={driver.id} disabled={!driver.eligible}>{driver.name}{driver.eligible ? '' : ` · ${reasons[driver.reason] || 'derzeit nicht geeignet'}`}</option>)}
      </select></label>
      {!data.can_release_to_fleet && <p>Die gesamte Kette wird gemeinsam einem Fahrer angeboten.</p>}
      <div className="ride-reassign-actions"><button type="submit" disabled={!choice || saving || loading}>{saving ? 'Wird gespeichert …' : 'Zuweisung bestätigen'}</button></div>
      <p className="ride-reassign-current">Ein neues Fahrerangebot ist erst nach Annahme bestätigt.</p>
    </form>}
  </section>;
}
